<!--
	Profile menu in the header: opens the access-key view and holds the theme choice.
	A plain popover with a button and a radio group; menu roles would promise arrow-key navigation.
-->
<script lang="ts">
	import AbleyButton from './AbleyButton.svelte';
	import MenuItem from './MenuItem.svelte';
	import IconUser from '#lib/assets/user.svg?raw';
	import { MediaQuery } from 'svelte/reactivity';
	import { themeStore, type ThemePreference } from '#lib/stores/themeStore.svelte.js';
	import SegmentedControl from './SegmentedControl.svelte';

	interface Props {
		/** Opens the access-key view. */
		onaccesskey: () => void;
	}

	const { onaccesskey }: Props = $props();

	const id = $props.id();
	let popoverEl: HTMLDivElement | undefined = $state();

	// Two states: follow the OS, or pin the opposite of it. A pinned choice stays offered even after the OS changes to match it.
	const osDark = new MediaQuery('(prefers-color-scheme: dark)');
	const pinnable = $derived<ThemePreference>(
		themeStore.preference !== 'system' ? themeStore.preference : osDark.current ? 'light' : 'dark'
	);
	const themes = $derived([
		{ value: 'system', label: 'System' },
		{ value: pinnable, label: pinnable === 'dark' ? 'Dark' : 'Light' }
	]);
</script>

<AbleyButton variant="text" popovertarget={id} anchorName="--profileMenu" aria-label="Profile menu">
	<span class="avatar" aria-hidden="true">{@html IconUser}</span>
	Profile
</AbleyButton>

<div {id} bind:this={popoverEl} class="menu" popover="auto">
	<MenuItem
		onclick={() => {
			popoverEl?.hidePopover();
			onaccesskey();
		}}>
		Access key
	</MenuItem>
	<div class="theme">
		<SegmentedControl
			label="Theme"
			size="sm"
			value={themeStore.preference}
			options={themes}
			onchange={(value) => themeStore.set(value as ThemePreference)} />
	</div>
</div>

<style>
	.avatar {
		border-radius: var(--radius-full);
		background-color: var(--text-muted);
		padding: var(--space-1);
		width: 1.75rem;
		height: 1.75rem;
		fill: var(--surface);
	}

	[popover].menu {
		position: fixed;
		position-anchor: --profileMenu;
		position-area: block-end span-inline-start;
		position-try-fallbacks: flip-inline;
		inset: auto;
		margin: var(--space-1) 0 0;
		box-shadow: var(--shadow-lg);
		border: 1px solid var(--border-color);
		border-radius: var(--radius);
		padding: var(--space-2) 0;
		min-width: 12rem;

		&:popover-open {
			display: flex;
			flex-direction: column;
			gap: var(--space-2);
		}
	}

	.theme {
		padding: var(--space-1) var(--space-3);
	}
</style>
